import {
  PROTOCOL_VERSION,
  isSessionEnvelope,
  type ArtifactManifestPayload,
  type ArtifactUrlPayload,
  type VideoModelId,
  type SessionEnvelope,
  type VideoTaskStatus,
} from "@bianhua/protocol";
import type { GatewayClient, GatewayState } from "./gateway-client";
import { isTauri } from "./platform";
import {
  buildVideoCreateRequest,
  VideoControlStore,
  type VideoControlSnapshot,
  type VideoControlState,
  type VideoCreateRequest,
} from "./video-control";

type RpcTaskResult = {
  id?: string;
  taskId?: string;
  status?: VideoTaskStatus;
  reason?: string;
  task?: { id?: string; taskId?: string; status?: VideoTaskStatus };
};

type RpcTaskSyncResult = {
  taskId: string;
  status: VideoTaskStatus;
  reason?: string;
  artifactManifest?: ArtifactManifestPayload;
  artifactUrl?: ArtifactUrlPayload;
};

type VideoTaskHistoryItem = {
  taskId: string | null;
  requestId: string;
  state: VideoControlState;
  prompt: string;
  modelId: VideoModelId;
  method: "text" | "image" | "reference";
  ratio: string;
  durationSeconds: number;
  generateAudio: boolean;
  resolution: string;
  assetIds: string[];
  createdAt: string;
  updatedAt: string;
  reason: string;
  localPath?: string;
  localSaveState?: "saving" | "failed";
  artifactManifest?: ArtifactManifestPayload;
  artifactUrl?: ArtifactUrlPayload;
};

type PersistedVideoTask = {
  principalId: string;
  taskId: string;
  state: "queued" | "running" | "recovering" | "archiving" | "cancelled" | "succeeded" | "failed" | "expired";
  requestId: string | null;
  modelId: VideoModelId | null;
  completedArtifactId: string | null;
  handoffId: string | null;
};

type PersistedPendingCreate = {
  request: VideoCreateRequest;
  handoffId: string | null;
};

type CapabilityRecord = {
  status?: unknown;
  source?: unknown;
  reason?: unknown;
  operations?: unknown;
};

function messageFrom(error: unknown): string {
  return error instanceof Error && error.message ? error.message : "视频服务请求失败";
}

function requestId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `video-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function safeWorkflowId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(normalized) ? normalized : null;
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
}

function taskResult(value: unknown): { taskId: string; status: VideoTaskStatus; reason?: string } {
  const result = (record(value) || {}) as RpcTaskResult;
  const nested = result.task || {};
  const taskId = String(result.taskId || result.id || nested.taskId || nested.id || "");
  const status = result.status || nested.status;
  if (!taskId || !["queued", "running", "cancelled", "succeeded", "failed", "expired"].includes(String(status))) {
    throw new Error("服务器没有返回有效的视频任务标识与状态");
  }
  const reason = typeof result.reason === "string" ? result.reason.trim().slice(0, 2_048) : "";
  return { taskId, status: status as VideoTaskStatus, ...(reason ? { reason } : {}) };
}

function validArtifactEnvelope(kind: "artifact.manifest" | "artifact.url", payload: unknown): boolean {
  return isSessionEnvelope({
    protocol: PROTOCOL_VERSION,
    id: `video-sync-${kind}`,
    kind,
    sentAt: "2026-01-01T00:00:00.000Z",
    payload,
  });
}

function taskSyncResult(value: unknown, expectedProjectId: string): RpcTaskSyncResult {
  const result = record(value);
  if (!result) throw new Error("服务器没有返回有效的视频同步结果");
  const allowed = new Set(["taskId", "status", "reason", "artifactManifest", "artifactUrl"]);
  if (Object.keys(result).some((key) => !allowed.has(key))) {
    throw new Error("服务器返回了未协商的视频同步字段");
  }
  const basic = taskResult(result);
  const hasManifest = Object.hasOwn(result, "artifactManifest");
  const hasUrl = Object.hasOwn(result, "artifactUrl");
  if (hasManifest !== hasUrl) throw new Error("服务器返回的视频产物信息不完整");
  if (!hasManifest) return basic;
  if (basic.status !== "succeeded") throw new Error("未完成的视频任务不能携带下载产物");
  if (!validArtifactEnvelope("artifact.manifest", result.artifactManifest)
    || !validArtifactEnvelope("artifact.url", result.artifactUrl)) {
    throw new Error("服务器返回的视频产物合同无效");
  }
  const artifactManifest = result.artifactManifest as ArtifactManifestPayload;
  const artifactUrl = result.artifactUrl as ArtifactUrlPayload;
  if (artifactManifest.artifactId !== artifactUrl.artifactId
    || artifactManifest.sha256 !== artifactUrl.sha256
    || artifactManifest.projectId !== expectedProjectId
    || artifactManifest.retention.serverStored !== true) {
    throw new Error("服务器返回的视频产物标识或校验值不一致");
  }
  return { ...basic, artifactManifest, artifactUrl };
}

function taskListResult(value: unknown, expectedProjectId: string): VideoTaskHistoryItem[] {
  const result = record(value);
  if (!result || !Array.isArray(result.tasks)) throw new Error("服务器没有返回有效的视频任务列表");
  return result.tasks.map((candidate) => {
    const item = record(candidate);
    if (!item) throw new Error("视频任务列表包含无效记录");
    const basic = taskResult(item);
    const requestId = String(item.requestId || "").trim();
    const prompt = String(item.prompt || "").trim().slice(0, 20_000);
    const modelId = String(item.modelId || "") as VideoModelId;
    const method = String(item.method || "text") as VideoTaskHistoryItem["method"];
    const createdAt = String(item.createdAt || "");
    const updatedAt = String(item.updatedAt || createdAt);
    if (!requestId || !["grok-imagine-video", "doubao-seedance-2-0-260128", "doubao-seedance-2-0-fast-260128", "doubao-seedance-2-5-260628"].includes(modelId)) {
      throw new Error("视频任务列表缺少请求或模型标识");
    }
    if (!["text", "image", "reference"].includes(method) || !Number.isFinite(Date.parse(createdAt))) {
      throw new Error("视频任务列表包含无效创作参数");
    }
    let artifactManifest: ArtifactManifestPayload | undefined;
    let artifactUrl: ArtifactUrlPayload | undefined;
    if (Object.hasOwn(item, "artifactManifest") || Object.hasOwn(item, "artifactUrl")) {
      const synced = taskSyncResult({
        taskId: basic.taskId,
        status: basic.status,
        artifactManifest: item.artifactManifest,
        artifactUrl: item.artifactUrl,
      }, expectedProjectId);
      artifactManifest = synced.artifactManifest;
      artifactUrl = synced.artifactUrl;
    }
    return {
      taskId: basic.taskId,
      requestId,
      state: basic.status,
      prompt,
      modelId,
      method,
      ratio: String(item.ratio || "adaptive"),
      durationSeconds: Number.isInteger(Number(item.durationSeconds)) ? Number(item.durationSeconds) : -1,
      generateAudio: item.generateAudio === true,
      resolution: String(item.resolution || "720p"),
      assetIds: Array.isArray(item.assetIds) ? item.assetIds.map(String).filter(Boolean) : [],
      createdAt,
      updatedAt: Number.isFinite(Date.parse(updatedAt)) ? updatedAt : createdAt,
      reason: String(item.reason || basic.reason || "").slice(0, 2_048),
      ...(artifactManifest && artifactUrl ? { artifactManifest, artifactUrl } : {}),
    };
  });
}

function dispatchVideoState(snapshot: VideoControlSnapshot): void {
  window.dispatchEvent(new CustomEvent("bianhua:video-state", {
    detail: {
      state: snapshot.state,
      taskId: snapshot.taskId,
      requestId: snapshot.requestId,
      modelId: snapshot.modelId,
      reason: snapshot.reason,
      canCreate: snapshot.canCreate,
      canCancelQueued: snapshot.canCancelQueued,
      canStopRunning: false,
    },
  }));
}

function dispatchVideoError(message: string): void {
  window.dispatchEvent(new CustomEvent("bianhua:video-error", { detail: { message } }));
}

function dispatchVideoHandoffLifecycle(
  type: "bianhua:video-handoff-submitted" | "bianhua:video-handoff-released",
  handoffId: string | null,
  principalId: string | null,
): void {
  if (!handoffId) return;
  window.dispatchEvent(new CustomEvent(type, {
    detail: { handoffId, ...(principalId ? { principalId } : {}) },
  }));
}

function createOutcomeMayExist(error: unknown, connectionState: GatewayState): boolean {
  if (connectionState !== "ready") return true;
  const candidate = record(error);
  if (candidate?.retryable === true) return true;
  if (candidate?.retryable === false) return false;
  return /超时|连接|会话|socket|timeout|network/i.test(messageFrom(error));
}

let activeVideoViewer: HTMLDialogElement | null = null;
let videoViewerBound = false;

function closeVideoViewer(): void {
  const dialog = activeVideoViewer;
  if (!dialog) return;
  dialog.querySelector<HTMLVideoElement>("video")?.pause();
  if (dialog.open) dialog.close();
  else {
    dialog.remove();
    activeVideoViewer = null;
  }
}

async function openVideoViewer(source: HTMLVideoElement | null): Promise<void> {
  if (!source?.src) return;
  closeVideoViewer();

  const dialog = document.createElement("dialog");
  dialog.className = "runtime-video-viewer";
  dialog.setAttribute("aria-labelledby", "runtime-video-viewer-title");
  dialog.innerHTML = `
    <div class="runtime-video-viewer-shell">
      <header>
        <strong id="runtime-video-viewer-title">视频预览</strong>
        <button type="button" class="runtime-video-viewer-close" aria-label="关闭视频预览">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </header>
      <div class="runtime-video-viewer-viewport">
        <video controls playsinline preload="metadata"></video>
      </div>
    </div>`;
  const viewerVideo = dialog.querySelector<HTMLVideoElement>("video");
  const closeButton = dialog.querySelector<HTMLButtonElement>(".runtime-video-viewer-close");
  if (!viewerVideo || !closeButton) return;
  viewerVideo.src = source.src;

  closeButton.addEventListener("click", closeVideoViewer);
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    closeVideoViewer();
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) closeVideoViewer();
  });
  dialog.addEventListener("close", () => {
    viewerVideo.pause();
    viewerVideo.removeAttribute("src");
    viewerVideo.load();
    dialog.remove();
    if (activeVideoViewer === dialog) activeVideoViewer = null;
  }, { once: true });

  document.body.append(dialog);
  activeVideoViewer = dialog;
  dialog.showModal();
  closeButton.focus({ preventScroll: true });
  await viewerVideo.play().catch(() => {});
}

export function bindVideoPreviewViewer(): void {
  if (videoViewerBound) return;
  videoViewerBound = true;
  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>("#video-panel [data-video-play], #video-panel .video-play")
      : null;
    if (!target || target.disabled) return;
    const card = target.closest("[data-video-task-key]") || document.querySelector("#video-panel");
    const source = card?.querySelector<HTMLVideoElement>("[data-video-element]") || null;
    void openVideoViewer(source);
  });
}

export class VideoRuntime {
  private readonly client: GatewayClient;
  private readonly store = new VideoControlStore();
  private completedArtifactId: string | null = null;
  private readonly artifactUrls = new Map<string, { url: string; expiresAt: string; sha256?: string }>();
  private readonly artifactManifests = new Map<string, ArtifactManifestPayload>();
  private pollTimer = 0;
  private pollInFlight = false;
  private pollRunId = 0;
  private pollAttempt = 0;
  private lastPolledStatus: VideoTaskStatus | null = null;
  private pollErrorReported = false;
  private pendingCreate: VideoCreateRequest | null = null;
  private pendingHandoffId: string | null = null;
  private activeHandoffId: string | null = null;
  private acceptedHandoffNotified = false;
  private principalId: string | null = null;
  private pendingPrincipalId: string | null = null;
  private readonly pendingCreateKey: string;
  private readonly activeTaskKey: string;
  private readonly localArtifactPathKey: string;
  private restoredActiveTask: PersistedVideoTask | null = null;
  private activeSyncTimer = 0;
  private activeSyncInFlight = false;
  private activeSyncRunId = 0;
  private pendingSyncRequested = false;
  private activeSyncAttempt = 0;
  private syncErrorReported = false;
  private videoAvailable = false;
  private videoCancelAvailable = false;
  private principalGeneration = 0;
  private artifactRetryTimer = 0;
  private artifactRetryAttempt = 0;
  private createRetryTimer = 0;
  private createInFlight = false;
  private createAttempt = 0;
  private exportInFlight = false;
  private readonly savedArtifactIds = new Set<string>();
  private readonly savedArtifactPaths = new Map<string, string>();
  private readonly historyArtifactSaveStates = new Map<string, "saving" | "failed">();
  private readonly historyArtifactSavesInFlight = new Set<string>();
  private readonly historyArtifactSaveErrorsReported = new Set<string>();
  private readonly taskHistory = new Map<string, VideoTaskHistoryItem>();
  private readonly taskArtifactIds = new Map<string, string>();
  private taskListAvailable = false;
  private taskListInFlight = false;
  private taskListRunId = 0;
  private taskListTimer = 0;

  constructor(client: GatewayClient) {
    this.client = client;
    const endpoint = client.config.gatewayUrl ? new URL(client.config.gatewayUrl).host : "unconfigured";
    this.pendingCreateKey = `bianhua-video-pending-create:${client.config.projectId}:${endpoint}`;
    this.activeTaskKey = `bianhua-video-active-task:${client.config.projectId}:${endpoint}`;
    this.localArtifactPathKey = `bianhua-video-local-artifacts:v1:${client.config.projectId}:${endpoint}`;
  }

  private emitTaskHistory(): void {
    const tasks = [...this.taskHistory.values()].map((task) => {
      const artifactId = task.artifactManifest?.artifactId || "";
      const localPath = artifactId ? this.savedArtifactPaths.get(artifactId) : undefined;
      const localSaveState = artifactId ? this.historyArtifactSaveStates.get(artifactId) : undefined;
      if (localPath) return { ...task, localPath, localSaveState: undefined };
      return localSaveState ? { ...task, localSaveState } : task;
    }).sort((left, right) => (
      Date.parse(left.createdAt) - Date.parse(right.createdAt)
      || left.requestId.localeCompare(right.requestId)
    ));
    window.dispatchEvent(new CustomEvent("bianhua:video-task-list", { detail: { tasks } }));
  }

  private registerTaskArtifacts(item: VideoTaskHistoryItem): void {
    if (!item.taskId || !item.artifactManifest || !item.artifactUrl) return;
    const manifest = item.artifactManifest;
    const transfer = item.artifactUrl;
    this.taskArtifactIds.set(item.taskId, manifest.artifactId);
    this.artifactManifests.set(manifest.artifactId, manifest);
    this.artifactUrls.set(transfer.artifactId, {
      url: transfer.url,
      expiresAt: transfer.expiresAt,
      sha256: transfer.sha256,
    });
  }

  private upsertTaskHistory(update: Partial<VideoTaskHistoryItem> & { requestId: string }): void {
    const previous = this.taskHistory.get(update.requestId);
    const pending = this.pendingCreate?.requestId === update.requestId ? this.pendingCreate : null;
    const now = new Date().toISOString();
    const next: VideoTaskHistoryItem = {
      taskId: update.taskId ?? previous?.taskId ?? null,
      requestId: update.requestId,
      state: update.state ?? previous?.state ?? "submitting",
      prompt: update.prompt ?? previous?.prompt ?? pending?.prompt ?? "",
      modelId: update.modelId ?? previous?.modelId ?? pending?.modelId ?? "grok-imagine-video",
      method: update.method ?? previous?.method ?? pending?.method ?? "text",
      ratio: update.ratio ?? previous?.ratio ?? pending?.ratio ?? "adaptive",
      durationSeconds: update.durationSeconds ?? previous?.durationSeconds ?? pending?.durationSeconds ?? -1,
      generateAudio: update.generateAudio ?? previous?.generateAudio ?? pending?.generateAudio ?? false,
      resolution: update.resolution ?? previous?.resolution ?? pending?.resolution ?? "720p",
      assetIds: update.assetIds ?? previous?.assetIds ?? pending?.assetIds ?? [],
      createdAt: update.createdAt ?? previous?.createdAt ?? now,
      updatedAt: update.updatedAt ?? now,
      reason: update.reason ?? previous?.reason ?? "",
      ...(update.artifactManifest && update.artifactUrl
        ? { artifactManifest: update.artifactManifest, artifactUrl: update.artifactUrl }
        : previous?.artifactManifest && previous.artifactUrl
          ? { artifactManifest: previous.artifactManifest, artifactUrl: previous.artifactUrl }
          : {}),
    };
    this.taskHistory.set(next.requestId, next);
    this.registerTaskArtifacts(next);
    this.emitTaskHistory();
  }

  private updateTaskHistoryFromSnapshot(snapshot: VideoControlSnapshot): void {
    if (!snapshot.requestId) return;
    const known = this.taskHistory.get(snapshot.requestId);
    if (!known && !this.pendingCreate) return;
    this.upsertTaskHistory({
      requestId: snapshot.requestId,
      taskId: snapshot.taskId,
      state: snapshot.state,
      reason: snapshot.reason,
    });
  }

  private async loadTaskHistory(): Promise<void> {
    if (!this.taskListAvailable || !this.principalId || this.client.state !== "ready" || this.taskListInFlight) return;
    const runId = ++this.taskListRunId;
    const generation = this.principalGeneration;
    this.taskListInFlight = true;
    try {
      const tasks = taskListResult(
        await this.client.rpc<unknown>("video/task/list", { limit: 50 }),
        this.client.config.projectId,
      );
      if (runId !== this.taskListRunId || generation !== this.principalGeneration) return;
      const localPending = [...this.taskHistory.values()].filter((item) => (
        !item.taskId || ["submitting", "recovering"].includes(item.state)
      ));
      this.taskHistory.clear();
      for (const task of tasks) {
        this.taskHistory.set(task.requestId, task);
        this.registerTaskArtifacts(task);
      }
      for (const pending of localPending) {
        if (!this.taskHistory.has(pending.requestId)) this.taskHistory.set(pending.requestId, pending);
      }
      this.emitTaskHistory();
      this.reconcileTaskArtifactSaves();
    } catch (error) {
      if (runId === this.taskListRunId && generation === this.principalGeneration) {
        dispatchVideoError(`视频任务列表同步失败：${messageFrom(error)}`);
      }
    } finally {
      if (runId === this.taskListRunId) {
        this.taskListInFlight = false;
        if ([...this.taskHistory.values()].some((task) => ["submitting", "recovering", "queued", "running", "archiving", "cancelling"].includes(task.state))) {
          this.scheduleTaskHistoryRefresh();
        }
      }
    }
  }

  private scheduleTaskHistoryRefresh(delayMs = 10_000): void {
    if (this.taskListTimer || !this.taskListAvailable || this.client.state !== "ready") return;
    this.taskListTimer = window.setTimeout(() => {
      this.taskListTimer = 0;
      void this.loadTaskHistory();
    }, delayMs);
  }

  private clearTaskHistoryRefresh(): void {
    if (this.taskListTimer) window.clearTimeout(this.taskListTimer);
    this.taskListTimer = 0;
  }

  bind(): void {
    this.store.subscribe((snapshot) => {
      const view = {
        ...snapshot,
        canCancelQueued: snapshot.canCancelQueued && this.videoCancelAvailable,
      };
      dispatchVideoState(view);
      this.updateTaskHistoryFromSnapshot(view);
    });
    document.addEventListener("bianhua:video-create", (event) => {
      this.create((event as CustomEvent).detail).catch(() => {});
    });
    document.addEventListener("bianhua:video-cancel-queued", () => {
      this.cancelQueued().catch(() => {});
    });
    this.client.addEventListener("state", (event) => {
      const state = String((event as CustomEvent<{ state: GatewayState }>).detail.state);
      if (state === "ready") {
        this.scheduleTaskSync(0);
        this.scheduleCreateRecovery(0);
        void this.loadTaskHistory();
      } else {
        this.clearPollTimer();
        this.clearTaskSyncTimer();
        this.clearCreateRetryTimer();
        this.clearTaskHistoryRefresh();
      }
      if (state === "error" || state === "offline") {
        window.dispatchEvent(new CustomEvent("bianhua:video-transport", { detail: { state } }));
      }
    });
    this.client.addEventListener("envelope", (event) => {
      this.onEnvelope((event as CustomEvent<SessionEnvelope>).detail);
    });
    window.addEventListener("bianhua:capabilities-snapshot", (event) => {
      const payload = record((event as CustomEvent).detail);
      if (payload) this.applyCapabilities(payload);
    });
    window.addEventListener("bianhua:artifact-retry-request", (event) => {
      const artifactId = String((event as CustomEvent).detail?.artifactId || "");
      if (artifactId && artifactId === this.completedArtifactId) this.scheduleArtifactRetry();
    });
    window.addEventListener("bianhua:artifact-save-complete", (event) => {
      const detail = (event as CustomEvent).detail;
      const artifactId = String(detail?.artifactId || "");
      if (!artifactId) return;
      this.savedArtifactIds.add(artifactId);
      const path = typeof detail?.result?.path === "string" ? detail.result.path.trim() : "";
      if (path) this.rememberSavedArtifactPath(artifactId, path);
      if (artifactId === this.completedArtifactId) {
        this.clearArtifactRetry();
      }
      this.emitTaskHistory();
    });
    window.addEventListener("bianhua:video-local-save-retry", (event) => {
      const artifactId = String((event as CustomEvent).detail?.artifactId || "");
      if (!artifactId) return;
      this.historyArtifactSaveStates.delete(artifactId);
      this.historyArtifactSaveErrorsReported.delete(artifactId);
      this.reconcileTaskArtifactSaves(artifactId);
    });
  }

  private reconcileTaskArtifactSaves(requestedArtifactId?: string): void {
    if (!isTauri() || !this.principalId) return;
    const saveManifest = window.BianhuaArtifactSync?.saveManifest;
    if (!saveManifest) return;
    const generation = this.principalGeneration;
    for (const task of this.taskHistory.values()) {
      const manifest = task.artifactManifest;
      const transfer = task.artifactUrl;
      if (task.state !== "succeeded" || !manifest || !transfer) continue;
      const artifactId = manifest.artifactId;
      if (requestedArtifactId && artifactId !== requestedArtifactId) continue;
      if (this.savedArtifactPaths.has(artifactId) || this.historyArtifactSavesInFlight.has(artifactId)) continue;
      this.historyArtifactSavesInFlight.add(artifactId);
      this.historyArtifactSaveStates.set(artifactId, "saving");
      this.emitTaskHistory();
      void saveManifest({
        artifactId,
        relativePath: manifest.relativePath,
        sizeBytes: manifest.size,
        sha256: manifest.sha256,
        mimeType: manifest.mediaType,
        download: { url: transfer.url, expiresAt: transfer.expiresAt },
      }).then((result) => {
        if (generation !== this.principalGeneration || !this.principalId) return;
        const path = typeof result?.path === "string" ? result.path.trim() : "";
        if (!path) throw new Error("客户端没有返回已保存视频的本地路径");
        this.rememberSavedArtifactPath(artifactId, path);
        this.historyArtifactSaveStates.delete(artifactId);
        this.historyArtifactSaveErrorsReported.delete(artifactId);
        this.emitTaskHistory();
      }).catch((error) => {
        if (generation !== this.principalGeneration || !this.principalId) return;
        this.historyArtifactSaveStates.set(artifactId, "failed");
        this.emitTaskHistory();
        if (!this.historyArtifactSaveErrorsReported.has(artifactId)) {
          this.historyArtifactSaveErrorsReported.add(artifactId);
          dispatchVideoError(`视频本地保存状态核对失败：${messageFrom(error)}`);
        }
      }).finally(() => {
        this.historyArtifactSavesInFlight.delete(artifactId);
      });
    }
  }

  private async create(rawDraft: unknown): Promise<void> {
    if (this.pendingCreate) {
      this.store.createOutcomeUnknown("上一项付费创建请求尚未确认，正在使用原幂等标识恢复；不会重复创建新任务");
      this.scheduleCreateRecovery(0);
      return;
    }
    const rawHandoffId = record(rawDraft)?.handoffId;
    const handoffId = safeWorkflowId(rawHandoffId);
    if (rawHandoffId !== undefined && !handoffId) {
      dispatchVideoError("视频交接标识无效，生成请求没有提交");
      return;
    }
    let request: VideoCreateRequest;
    try {
      request = buildVideoCreateRequest(rawDraft, requestId());
    } catch (error) {
      dispatchVideoError(messageFrom(error));
      return;
    }
    if (this.client.state !== "ready") {
      dispatchVideoError("服务器尚未连接，视频任务没有提交");
      return;
    }
    if (!this.principalId) {
      dispatchVideoError("服务器未提供匿名账户恢复标识，为避免跨账户重复计费，视频任务没有提交");
      return;
    }
    if (!this.store.beginCreate(request)) {
      dispatchVideoError("视频服务尚未配置，或当前已有任务正在处理");
      return;
    }
    this.clearPollTimer();
    this.clearTaskSyncTimer();
    this.clearArtifactRetry();
    this.clearPersistedActiveTask();
    this.completedArtifactId = null;
    this.artifactManifests.clear();
    this.activeHandoffId = null;
    this.acceptedHandoffNotified = false;
    this.pollAttempt = 0;
    this.lastPolledStatus = null;
    this.pollErrorReported = false;
    this.pendingCreate = request;
    this.pendingHandoffId = handoffId;
    this.pendingPrincipalId = this.principalId;
    if (!this.persistPendingCreate()) {
      const message = "无法写入付费任务恢复日志；为避免重复计费，视频请求没有发送";
      this.clearPendingCreate();
      this.store.createFailed(message);
      this.upsertTaskHistory({
        requestId: request.requestId,
        state: "failed",
        prompt: request.prompt,
        modelId: request.modelId,
        method: request.method,
        ratio: request.ratio,
        durationSeconds: request.durationSeconds,
        generateAudio: request.generateAudio,
        resolution: request.resolution,
        assetIds: request.assetIds,
        reason: message,
      });
      dispatchVideoError(message);
      return;
    }
    this.upsertTaskHistory({
      requestId: request.requestId,
      state: "submitting",
      prompt: request.prompt,
      modelId: request.modelId,
      method: request.method,
      ratio: request.ratio,
      durationSeconds: request.durationSeconds,
      generateAudio: request.generateAudio,
      resolution: request.resolution,
      assetIds: request.assetIds,
      reason: "正在等待服务器确认视频任务",
    });
    dispatchVideoHandoffLifecycle(
      "bianhua:video-handoff-submitted",
      this.pendingHandoffId,
      this.pendingPrincipalId,
    );
    this.createAttempt = 0;
    await this.submitPendingCreate();
  }

  private async submitPendingCreate(): Promise<void> {
    const request = this.pendingCreate;
    if (!request || this.createInFlight || this.client.state !== "ready") return;
    this.createInFlight = true;
    try {
      const result = await this.client.rpc<RpcTaskResult>("video/task/create", request);
      if (this.pendingCreate?.requestId !== request.requestId) return;
      const accepted = taskResult(result);
      this.commitAcceptedTask(accepted.taskId, accepted.status);
      this.lastPolledStatus = accepted.status;
      if (accepted.status === "queued" || accepted.status === "running") this.schedulePoll();
    } catch (error) {
      const message = messageFrom(error);
      if (this.pendingCreate?.requestId !== request.requestId) return;
      if (createOutcomeMayExist(error, this.client.state)) {
        this.createAttempt += 1;
        this.store.createOutcomeUnknown("服务器响应中断，正在用同一幂等标识确认任务；不会重复计费创建");
      } else {
        dispatchVideoHandoffLifecycle(
          "bianhua:video-handoff-released",
          this.pendingHandoffId,
          this.pendingPrincipalId,
        );
        this.clearPendingCreate();
        this.store.createFailed(message);
        this.upsertTaskHistory({ requestId: request.requestId, state: "failed", reason: message });
        dispatchVideoError(message);
      }
    } finally {
      this.createInFlight = false;
      if (this.pendingCreate) this.scheduleCreateRecovery();
    }
  }

  private commitAcceptedTask(taskId: string, status: VideoTaskStatus): void {
    const acceptedRequest = this.pendingCreate;
    if (status === "succeeded") this.store.markArchiving(taskId);
    else this.store.createAccepted(taskId, status);
    if (acceptedRequest) {
      this.upsertTaskHistory({
        requestId: acceptedRequest.requestId,
        taskId,
        state: status === "succeeded" ? "archiving" : status,
        reason: status === "succeeded" ? "服务器正在留存并校验原片" : "",
      });
    }
    this.activeHandoffId = this.pendingHandoffId;
    if (this.persistActiveTask()) {
      this.notifyAcceptedHandoff(taskId);
      if (acceptedRequest) {
        window.dispatchEvent(new CustomEvent("bianhua:video-create-accepted", {
          detail: {
            requestId: acceptedRequest.requestId,
            taskId,
            prompt: acceptedRequest.prompt,
          },
        }));
      }
      this.clearPendingCreate();
      return;
    }
    // Keep the already-durable pending journal. Replaying the same requestId is
    // safer than losing a paid task between provider acceptance and active-task persistence.
    dispatchVideoError("任务已被服务商接收，但本地活动任务日志写入失败；将保留原幂等请求自动恢复");
  }

  private notifyAcceptedHandoff(taskId: string): void {
    if (!this.activeHandoffId || this.acceptedHandoffNotified) return;
    this.acceptedHandoffNotified = true;
    window.dispatchEvent(new CustomEvent("bianhua:video-handoff-accepted", {
      detail: {
        handoffId: this.activeHandoffId,
        taskId,
        ...(this.principalId ? { principalId: this.principalId } : {}),
      },
    }));
  }

  private async cancelQueued(): Promise<void> {
    const snapshot = this.store.snapshot();
    if (!snapshot.taskId || !this.store.beginCancelQueued()) {
      dispatchVideoError(snapshot.state === "running"
        ? "服务商已经开始生成，官方接口不支持中途停止"
        : "当前没有可取消的排队任务");
      return;
    }
    this.clearPollTimer();
    try {
      const result = await this.client.rpc<RpcTaskResult>("video/task/cancelQueued", { taskId: snapshot.taskId });
      const cancelled = taskResult(result);
      if (cancelled.status === "succeeded") {
        this.store.markArchiving(cancelled.taskId);
      } else {
        this.store.applyTaskEvent({ taskId: cancelled.taskId, status: cancelled.status });
      }
      this.persistActiveTask();
      if (cancelled.status === "queued" || cancelled.status === "running") this.schedulePoll(0);
    } catch (error) {
      const message = messageFrom(error);
      this.store.cancelFailed(message);
      dispatchVideoError(message);
      this.schedulePoll(0);
    }
  }

  private onEnvelope(envelope: SessionEnvelope): void {
    const payload = record(envelope.payload);
    if (!payload) return;
    if (envelope.kind === "connection.welcome") {
      const principalId = typeof payload.principalId === "string" ? payload.principalId : "";
      const previousPrincipalId = this.principalId;
      const nextPrincipalId = principalId || null;
      const principalChanged = previousPrincipalId !== nextPrincipalId;
      this.principalId = nextPrincipalId;
      if (principalChanged) {
        this.savedArtifactIds.clear();
        this.savedArtifactPaths.clear();
        this.historyArtifactSaveStates.clear();
        this.historyArtifactSavesInFlight.clear();
        this.historyArtifactSaveErrorsReported.clear();
        if (nextPrincipalId) this.restoreSavedArtifactPaths(nextPrincipalId);
      }
      if (principalChanged && previousPrincipalId) {
        this.principalGeneration += 1;
        this.clearPollTimer();
        this.clearTaskSyncTimer();
        this.pollRunId += 1;
        this.pollInFlight = false;
        this.activeSyncRunId += 1;
        this.activeSyncInFlight = false;
        this.pendingSyncRequested = false;
        this.clearCreateRetryTimer();
        this.completedArtifactId = null;
        this.artifactUrls.clear();
        this.artifactManifests.clear();
        this.taskHistory.clear();
        this.taskArtifactIds.clear();
        this.clearTaskHistoryRefresh();
        this.emitTaskHistory();
        this.pendingHandoffId = null;
        this.activeHandoffId = null;
        this.acceptedHandoffNotified = false;
        this.activeSyncAttempt = 0;
        this.syncErrorReported = false;
        this.clearArtifactRetry();
        this.pendingCreate = null;
        this.pendingPrincipalId = null;
        this.createAttempt = 0;
        this.store.resetForPrincipalChange();
      }
      if (this.principalId && !this.pendingCreate) {
        const restoredPending = this.readPersistedPendingCreate(this.principalId);
        if (restoredPending) {
          this.pendingCreate = restoredPending.request;
          this.pendingHandoffId = restoredPending.handoffId;
          this.pendingPrincipalId = this.principalId;
          this.store.restorePendingCreate(restoredPending.request);
          dispatchVideoHandoffLifecycle(
            "bianhua:video-handoff-submitted",
            restoredPending.handoffId,
            this.principalId,
          );
        }
      }
      this.restoredActiveTask = this.principalId
        ? this.readPersistedActiveTask(this.principalId)
        : null;
      this.scheduleCreateRecovery(0);
      this.hydratePersistedActiveTask();
      this.scheduleTaskSync(0);
      void this.loadTaskHistory();
      return;
    }
    if (envelope.kind === "capabilities.snapshot") {
      this.applyCapabilities(payload);
      return;
    }
    if (envelope.kind === "domain.event") {
      const type = String(payload.type || "");
      if (!type.startsWith("video.task.")) return;
      const data = record(payload.data);
      if (!data) return;
      let acceptedPendingEvent = false;
      if (this.pendingCreate && !this.store.snapshot().taskId) {
        if (type !== "video.task.created" || data.requestId !== this.pendingCreate.requestId) return;
        acceptedPendingEvent = true;
      }
      if (!this.store.snapshot().taskId && !acceptedPendingEvent) return;
      const retryableFailure = type === "video.task.failed"
        && record(data.error)?.retryable === true;
      if (retryableFailure) {
        const taskId = String(data.taskId || "");
        const recoveryMessage = String(record(data.error)?.message || "服务器正在恢复视频任务");
        if (!this.store.markTaskRecovering(taskId, recoveryMessage)) return;
        this.persistActiveTask();
        this.clearPollTimer();
        this.scheduleTaskSync(0);
        return;
      }
      if (!this.store.applyTaskEvent(data)) return;
      if (type === "video.task.completed") {
        this.completedArtifactId = String(data.artifactId || "") || null;
      }
      if (acceptedPendingEvent) this.activeHandoffId = this.pendingHandoffId;
      const activePersisted = this.persistActiveTask();
      if (acceptedPendingEvent && activePersisted) {
        const acceptedRequest = this.pendingCreate;
        this.notifyAcceptedHandoff(String(data.taskId || ""));
        if (acceptedRequest) {
          this.upsertTaskHistory({
            requestId: acceptedRequest.requestId,
            taskId: String(data.taskId || "") || null,
            state: String(data.status || "queued") as VideoControlState,
            reason: "",
          });
          window.dispatchEvent(new CustomEvent("bianhua:video-create-accepted", {
            detail: {
              requestId: acceptedRequest.requestId,
              taskId: String(data.taskId || ""),
              prompt: acceptedRequest.prompt,
            },
          }));
        }
        this.clearPendingCreate();
      }
      else if (acceptedPendingEvent && !activePersisted) {
        dispatchVideoError("任务已被服务商接收，但本地活动任务日志写入失败；将保留原幂等请求自动恢复");
      }
      const status = String(data.status || "");
      if (status === "queued" || status === "running") {
        this.lastPolledStatus = status as VideoTaskStatus;
        this.schedulePoll();
      } else {
        this.clearPollTimer();
      }
      if (type === "video.task.completed") this.publishArtifactIfReady();
      return;
    }
    if (envelope.kind === "artifact.manifest") {
      if (!validArtifactEnvelope("artifact.manifest", payload)) return;
      const manifest = payload as unknown as ArtifactManifestPayload;
      if (manifest.projectId !== this.client.config.projectId || manifest.retention.serverStored !== true) return;
      this.artifactManifests.set(manifest.artifactId, manifest);
      this.publishArtifactIfReady();
      return;
    }
    if (envelope.kind === "artifact.url") {
      const artifactId = String(payload.artifactId || "");
      const url = String(payload.url || "");
      const expiresAt = String(payload.expiresAt || "");
      const sha256 = String(payload.sha256 || "");
      if (artifactId && url && expiresAt) {
        this.artifactUrls.set(artifactId, sha256 ? { url, expiresAt, sha256 } : { url, expiresAt });
        this.publishArtifactIfReady();
      }
    }
  }

  private applyCapabilities(payload: Record<string, unknown>): void {
    const modes = record(payload.modes);
    const videoMode = record(modes?.video) as CapabilityRecord | null;
    const features = Array.isArray(payload.features) ? payload.features.map(record).filter(Boolean) : [];
    const feature = features.find((entry) => entry?.id === "video.grok")
      || features.find((entry) => entry?.id === "video.seedance") as CapabilityRecord | undefined;
    const available = videoMode?.status === "available"
      && videoMode.source === "provider_api"
      && feature?.status === "available"
      && feature.source === "provider_api";
    const operations = Array.isArray(feature?.operations) ? feature.operations.map(String) : [];
    const requiredOperations = [
      "video/task/create",
      "video/task/get",
      "video/task/sync",
    ];
    const hasLifecycle = requiredOperations.every((operation) => operations.includes(operation));
    this.videoAvailable = Boolean(available && hasLifecycle);
    this.taskListAvailable = operations.includes("video/task/list");
    this.videoCancelAvailable = operations.includes("video/task/cancelQueued");
    this.store.setCapability(this.videoAvailable, String(videoMode?.reason || feature?.reason || ""));
    if (this.videoAvailable) {
      this.scheduleCreateRecovery(0);
      this.scheduleTaskSync(0);
      void this.loadTaskHistory();
    }
  }

  private scheduleCreateRecovery(delayMs?: number): void {
    if (
      !this.pendingCreate
      || !this.principalId
      || this.pendingPrincipalId !== this.principalId
      || this.createInFlight
      || this.createRetryTimer
      || this.client.state !== "ready"
    ) return;
    const delay = delayMs ?? Math.min(30_000, 2_000 * 1.8 ** Math.min(this.createAttempt, 5));
    this.createRetryTimer = window.setTimeout(() => {
      this.createRetryTimer = 0;
      void this.submitPendingCreate();
    }, delay);
  }

  private clearCreateRetryTimer(): void {
    if (!this.createRetryTimer) return;
    window.clearTimeout(this.createRetryTimer);
    this.createRetryTimer = 0;
  }

  private persistPendingCreate(): boolean {
    if (!this.pendingCreate || !this.pendingPrincipalId) return false;
    try {
      const storageKey = this.pendingCreateStorageKey(this.pendingPrincipalId);
      const serialized = JSON.stringify({
        principalId: this.pendingPrincipalId,
        request: this.pendingCreate,
        handoffId: this.pendingHandoffId,
      });
      localStorage.setItem(storageKey, serialized);
      return localStorage.getItem(storageKey) === serialized;
    } catch {
      return false;
    }
  }

  private pendingCreateStorageKey(principalId: string): string {
    return `${this.pendingCreateKey}:${principalId}`;
  }

  private readPersistedPendingCreate(principalId: string): PersistedPendingCreate | null {
    const storageKey = this.pendingCreateStorageKey(principalId);
    try {
      const serialized = localStorage.getItem(storageKey);
      if (!serialized) return null;
      const parsed = JSON.parse(serialized) as Record<string, unknown>;
      const rawRequest = record(parsed.request);
      const storedPrincipalId = typeof parsed.principalId === "string" ? parsed.principalId : "";
      if (!rawRequest || storedPrincipalId !== principalId) throw new Error("待恢复视频请求缺少账户绑定");
      const rawHandoffId = parsed.handoffId;
      const handoffId = rawHandoffId == null ? null : safeWorkflowId(rawHandoffId);
      if (rawHandoffId != null && !handoffId) throw new Error("待恢复视频请求的交接标识无效");
      return {
        request: buildVideoCreateRequest(rawRequest, String(rawRequest.requestId || "")),
        handoffId,
      };
    } catch {
      try { localStorage.removeItem(storageKey); } catch { /* no-op */ }
      return null;
    }
  }

  private clearPendingCreate(): void {
    const storageKey = this.pendingPrincipalId
      ? this.pendingCreateStorageKey(this.pendingPrincipalId)
      : null;
    this.pendingCreate = null;
    this.pendingHandoffId = null;
    this.pendingPrincipalId = null;
    this.createAttempt = 0;
    this.clearCreateRetryTimer();
    if (!storageKey) return;
    try { localStorage.removeItem(storageKey); } catch { /* no-op */ }
  }

  private activeTaskStorageKey(principalId: string): string {
    return `${this.activeTaskKey}:${principalId}`;
  }

  private localArtifactPathStorageKey(principalId: string): string {
    return `${this.localArtifactPathKey}:${principalId}`;
  }

  private restoreSavedArtifactPaths(principalId: string): void {
    try {
      const serialized = localStorage.getItem(this.localArtifactPathStorageKey(principalId));
      if (!serialized) return;
      const parsed = JSON.parse(serialized) as Record<string, unknown>;
      for (const [artifactId, rawPath] of Object.entries(parsed)) {
        const path = typeof rawPath === "string" ? rawPath.trim() : "";
        if (!artifactId || artifactId.length > 128 || !path || path.length > 2_048) continue;
        this.savedArtifactIds.add(artifactId);
        this.savedArtifactPaths.set(artifactId, path);
      }
    } catch {
      try { localStorage.removeItem(this.localArtifactPathStorageKey(principalId)); } catch { /* no-op */ }
    }
  }

  private rememberSavedArtifactPath(artifactId: string, path: string): void {
    this.savedArtifactIds.add(artifactId);
    this.savedArtifactPaths.delete(artifactId);
    this.savedArtifactPaths.set(artifactId, path);
    while (this.savedArtifactPaths.size > 100) {
      const oldest = this.savedArtifactPaths.keys().next().value;
      if (typeof oldest !== "string") break;
      this.savedArtifactPaths.delete(oldest);
    }
    if (!this.principalId) return;
    try {
      localStorage.setItem(
        this.localArtifactPathStorageKey(this.principalId),
        JSON.stringify(Object.fromEntries(this.savedArtifactPaths)),
      );
    } catch { /* best-effort local display metadata */ }
  }

  private readPersistedActiveTask(principalId: string): PersistedVideoTask | null {
    const storageKey = this.activeTaskStorageKey(principalId);
    try {
      const serialized = localStorage.getItem(storageKey);
      if (!serialized) return null;
      const parsed = JSON.parse(serialized) as Record<string, unknown>;
      const states = new Set(["queued", "running", "recovering", "archiving", "cancelled", "succeeded", "failed", "expired"]);
      const models = new Set([
        "grok-imagine-video",
        "doubao-seedance-2-0-260128",
        "doubao-seedance-2-0-fast-260128",
        "doubao-seedance-2-5-260628",
      ]);
      const storedPrincipalId = typeof parsed.principalId === "string" ? parsed.principalId : "";
      const taskId = typeof parsed.taskId === "string" ? parsed.taskId.trim() : "";
      const state = typeof parsed.state === "string" ? parsed.state : "";
      const modelId = typeof parsed.modelId === "string" && models.has(parsed.modelId)
        ? parsed.modelId as VideoModelId
        : null;
      const rawHandoffId = parsed.handoffId;
      const handoffId = rawHandoffId == null ? null : safeWorkflowId(rawHandoffId);
      if (storedPrincipalId !== principalId || !taskId || !states.has(state)) {
        throw new Error("已保存的视频任务缺少安全恢复字段");
      }
      if (rawHandoffId != null && !handoffId) throw new Error("已保存的视频交接标识无效");
      return {
        principalId: storedPrincipalId,
        taskId,
        state: state as PersistedVideoTask["state"],
        requestId: typeof parsed.requestId === "string" ? parsed.requestId : null,
        modelId,
        completedArtifactId: typeof parsed.completedArtifactId === "string"
          ? parsed.completedArtifactId
          : null,
        handoffId,
      };
    } catch {
      try { localStorage.removeItem(storageKey); } catch { /* no-op */ }
      return null;
    }
  }

  private hydratePersistedActiveTask(): void {
    const restored = this.restoredActiveTask;
    if (!restored || !this.principalId || restored.principalId !== this.principalId) return;
    if (this.pendingCreate || this.store.snapshot().taskId) {
      this.restoredActiveTask = null;
      return;
    }
    const accepted = this.store.restoreTask({
      taskId: restored.taskId,
      state: restored.state,
      requestId: restored.requestId,
      modelId: restored.modelId,
    });
    if (!accepted) {
      this.clearPersistedActiveTask();
      return;
    }
    this.completedArtifactId = restored.completedArtifactId;
    this.activeHandoffId = restored.handoffId;
    this.acceptedHandoffNotified = false;
    this.notifyAcceptedHandoff(restored.taskId);
    this.restoredActiveTask = null;
  }

  private persistActiveTask(): boolean {
    const snapshot = this.store.snapshot();
    const persistable = new Set<VideoControlState>([
      "queued",
      "running",
      "recovering",
      "archiving",
      "cancelled",
      "succeeded",
      "failed",
      "expired",
    ]);
    if (!this.principalId || !snapshot.taskId || !persistable.has(snapshot.state)) return false;
    const value: PersistedVideoTask = {
      principalId: this.principalId,
      taskId: snapshot.taskId,
      state: snapshot.state as PersistedVideoTask["state"],
      requestId: snapshot.requestId,
      modelId: snapshot.modelId,
      completedArtifactId: this.completedArtifactId,
      handoffId: this.activeHandoffId,
    };
    try {
      const storageKey = this.activeTaskStorageKey(this.principalId);
      const serialized = JSON.stringify(value);
      localStorage.setItem(storageKey, serialized);
      return localStorage.getItem(storageKey) === serialized;
    } catch {
      return false;
    }
  }

  private clearPersistedActiveTask(): void {
    this.restoredActiveTask = null;
    this.activeHandoffId = null;
    this.acceptedHandoffNotified = false;
    if (!this.principalId) return;
    try { localStorage.removeItem(this.activeTaskStorageKey(this.principalId)); } catch { /* no-op */ }
  }

  private scheduleTaskSync(delayMs = 0): void {
    const snapshot = this.store.snapshot();
    if (
      !this.videoAvailable
      || !this.principalId
      || this.client.state !== "ready"
      || this.activeSyncTimer
      || !snapshot.taskId
    ) return;
    if (this.activeSyncInFlight) {
      this.pendingSyncRequested = true;
      return;
    }
    this.activeSyncTimer = window.setTimeout(() => {
      this.activeSyncTimer = 0;
      void this.syncTask();
    }, delayMs);
  }

  private async syncTask(): Promise<void> {
    const before = this.store.snapshot();
    if (
      this.activeSyncInFlight
      || this.client.state !== "ready"
      || !this.videoAvailable
      || !this.principalId
      || !before.taskId
    ) return;
    const runId = ++this.activeSyncRunId;
    const generation = this.principalGeneration;
    this.pendingSyncRequested = false;
    this.activeSyncInFlight = true;
    let retry = false;
    let pollAfterSync = false;
    try {
      const result = taskSyncResult(
        await this.client.rpc<unknown>("video/task/sync", { taskId: before.taskId }),
        this.client.config.projectId,
      );
      if (
        runId !== this.activeSyncRunId
        || generation !== this.principalGeneration
        || result.taskId !== before.taskId
        || this.store.snapshot().taskId !== before.taskId
      ) return;
      this.syncErrorReported = false;
      if (result.status === "succeeded" && result.artifactManifest && result.artifactUrl) {
        this.activeSyncAttempt = 0;
        this.completedArtifactId = result.artifactManifest.artifactId;
        this.artifactManifests.set(result.artifactManifest.artifactId, result.artifactManifest);
        this.artifactUrls.set(result.artifactUrl.artifactId, {
          url: result.artifactUrl.url,
          expiresAt: result.artifactUrl.expiresAt,
          sha256: result.artifactUrl.sha256,
        });
        window.dispatchEvent(new CustomEvent("bianhua:artifact-sync-recovered", {
          detail: {
            manifest: result.artifactManifest,
            url: result.artifactUrl,
          },
        }));
        this.store.applyTaskEvent({
          taskId: result.taskId,
          status: "succeeded",
          label: "服务器原片已经留存并校验完成",
        });
        this.persistActiveTask();
        this.publishArtifactIfReady();
      } else if (result.status === "succeeded") {
        this.activeSyncAttempt += 1;
        this.store.markArchiving(result.taskId);
        this.persistActiveTask();
        retry = true;
      } else {
        this.activeSyncAttempt = 0;
        this.store.applyTaskEvent({
          taskId: result.taskId,
          status: result.status,
          label: result.reason || (
            result.status === "failed" || result.status === "expired"
              ? "服务器已恢复任务的最终状态"
              : ""
          ),
        });
        this.persistActiveTask();
        if (result.status === "queued" || result.status === "running") {
          this.lastPolledStatus = result.status;
          pollAfterSync = true;
        }
      }
    } catch (error) {
      if (
        runId === this.activeSyncRunId
        && generation === this.principalGeneration
        && this.store.snapshot().taskId === before.taskId
      ) {
        const candidate = record(error);
        const code = String(candidate?.code || "");
        const taskMissing = code === "VIDEO_TASK_NOT_FOUND";
        if (taskMissing) {
          const message = messageFrom(error);
          this.completedArtifactId = null;
          this.artifactUrls.clear();
          this.artifactManifests.clear();
          this.store.applyTaskEvent({
            taskId: before.taskId,
            status: "failed",
            error: { message },
          });
          this.persistActiveTask();
          dispatchVideoError(message);
        } else if (candidate?.retryable === false) {
          const message = messageFrom(error);
          this.store.markTaskRecovering(
            before.taskId,
            `自动恢复已暂停，服务器尚未证明任务终止：${message}`,
          );
          this.persistActiveTask();
          dispatchVideoError("视频任务仍被锁定，服务器尚未证明其终止；请检查网关或重新连接后恢复，勿重复创建");
        } else {
          this.activeSyncAttempt += 1;
          retry = true;
          if (!this.syncErrorReported) {
            this.syncErrorReported = true;
            dispatchVideoError(`视频任务恢复暂时失败，将自动重试：${messageFrom(error)}`);
          }
        }
      }
    } finally {
      if (runId === this.activeSyncRunId && generation === this.principalGeneration) {
        this.activeSyncInFlight = false;
        if (pollAfterSync) this.schedulePoll(0);
        if (this.pendingSyncRequested) {
          this.pendingSyncRequested = false;
          this.scheduleTaskSync(0);
        } else if (retry) {
          const delay = Math.min(30_000, 2_000 * 1.8 ** Math.min(this.activeSyncAttempt, 5));
          this.scheduleTaskSync(delay);
        }
      }
    }
  }

  private clearTaskSyncTimer(): void {
    if (!this.activeSyncTimer) return;
    window.clearTimeout(this.activeSyncTimer);
    this.activeSyncTimer = 0;
  }

  private scheduleArtifactRetry(): void {
    if (this.artifactRetryTimer || !this.completedArtifactId) return;
    const delay = Math.min(30_000, 1_000 * 2 ** Math.min(this.artifactRetryAttempt, 5));
    this.artifactRetryAttempt += 1;
    this.artifactRetryTimer = window.setTimeout(() => {
      this.artifactRetryTimer = 0;
      this.scheduleTaskSync(0);
    }, delay);
  }

  private clearArtifactRetry(): void {
    if (this.artifactRetryTimer) window.clearTimeout(this.artifactRetryTimer);
    this.artifactRetryTimer = 0;
    this.artifactRetryAttempt = 0;
  }

  private schedulePoll(delayMs?: number): void {
    const snapshot = this.store.snapshot();
    if (
      this.client.state !== "ready"
      || this.pollTimer
      || this.pollInFlight
      || this.activeSyncInFlight
      || !snapshot.taskId
      || !["queued", "running"].includes(snapshot.state)
    ) return;
    const delay = delayMs ?? Math.min(15_000, 2_000 * 1.55 ** Math.min(this.pollAttempt, 6));
    this.pollTimer = window.setTimeout(() => {
      this.pollTimer = 0;
      void this.pollTask();
    }, delay);
  }

  private async pollTask(): Promise<void> {
    const before = this.store.snapshot();
    if (
      this.pollInFlight
      || this.activeSyncInFlight
      || this.client.state !== "ready"
      || !before.taskId
      || !["queued", "running"].includes(before.state)
    ) return;
    const runId = ++this.pollRunId;
    const generation = this.principalGeneration;
    this.pollInFlight = true;
    let shouldContinue = false;
    try {
      const result = taskResult(await this.client.rpc<RpcTaskResult>("video/task/get", { taskId: before.taskId }));
      const current = this.store.snapshot();
      if (
        runId !== this.pollRunId
        || generation !== this.principalGeneration
        || current.taskId !== before.taskId
        || result.taskId !== before.taskId
      ) return;
      this.pollErrorReported = false;
      this.pollAttempt = result.status === this.lastPolledStatus ? this.pollAttempt + 1 : 0;
      this.lastPolledStatus = result.status;
      if (result.status === "succeeded") {
        // Provider completion is not client delivery. Wait for the gateway to archive,
        // verify and publish a video.task.completed event with a durable artifactId.
        this.store.markArchiving(result.taskId);
        this.persistActiveTask();
        this.scheduleTaskSync(0);
      } else {
        this.store.applyTaskEvent({
          taskId: result.taskId,
          status: result.status,
          ...(result.reason ? { label: result.reason } : {}),
        });
        this.persistActiveTask();
        shouldContinue = result.status === "queued" || result.status === "running";
      }
    } catch (error) {
      const current = this.store.snapshot();
      if (runId === this.pollRunId && generation === this.principalGeneration) {
        shouldContinue = current.taskId === before.taskId && ["queued", "running"].includes(current.state);
        this.pollAttempt += 1;
        if (!this.pollErrorReported) {
          this.pollErrorReported = true;
          dispatchVideoError(`任务状态刷新暂时失败，将自动重试：${messageFrom(error)}`);
        }
      }
    } finally {
      if (runId === this.pollRunId && generation === this.principalGeneration) {
        this.pollInFlight = false;
        if (shouldContinue) this.schedulePoll();
      }
    }
  }

  private clearPollTimer(): void {
    if (!this.pollTimer) return;
    window.clearTimeout(this.pollTimer);
    this.pollTimer = 0;
  }

  private publishArtifactIfReady(): void {
    if (!this.completedArtifactId) return;
    const transfer = this.artifactUrls.get(this.completedArtifactId);
    if (!transfer || Date.parse(transfer.expiresAt) <= Date.now()) return;
    const manifest = this.artifactManifests.get(this.completedArtifactId);
    if (!manifest
      || manifest.projectId !== this.client.config.projectId
      || manifest.retention.serverStored !== true
      || manifest.sha256 !== transfer.sha256) return;
    const snapshot = this.store.snapshot();
    if (snapshot.requestId) {
      this.upsertTaskHistory({
        requestId: snapshot.requestId,
        taskId: snapshot.taskId,
        state: "succeeded",
        reason: "",
        artifactManifest: manifest,
        artifactUrl: {
          artifactId: manifest.artifactId,
          url: transfer.url,
          expiresAt: transfer.expiresAt,
          sha256: transfer.sha256 || manifest.sha256,
        },
      });
    }
    if (snapshot.taskId) this.taskArtifactIds.set(snapshot.taskId, this.completedArtifactId);
    window.dispatchEvent(new CustomEvent("bianhua:video-artifact", {
      detail: {
        artifactId: this.completedArtifactId,
        taskId: snapshot.taskId,
        requestId: snapshot.requestId,
        ...transfer,
        ...{
          projectId: manifest.projectId,
          relativePath: manifest.relativePath,
          fileName: manifest.fileName,
          mediaType: manifest.mediaType,
          size: manifest.size,
          artifactRef: {
            assetId: manifest.artifactId,
            artifactId: manifest.artifactId,
            relativePath: manifest.relativePath,
            mediaType: manifest.mediaType,
            sha256: manifest.sha256,
          },
        },
      },
    }));
    this.updateDownloadButtons();
  }

  private updateDownloadButtons(): void {
    if (typeof document.querySelectorAll !== "function") return;
    for (const button of document.querySelectorAll<HTMLButtonElement>("#video-panel [data-video-download]")) {
      const artifactId = String(button.dataset.artifactId || "");
      const transfer = artifactId ? this.artifactUrls.get(artifactId) : null;
      const available = Boolean(artifactId && transfer);
      const locallyReady = available && (!isTauri() || this.savedArtifactIds.has(artifactId));
      button.dataset.artifactReady = String(locallyReady);
      if (!this.exportInFlight) button.disabled = !available;
    }
  }

  private async refreshHistoryTask(taskId: string): Promise<string | null> {
    if (!taskId || this.client.state !== "ready") return null;
    try {
      const result = taskSyncResult(
        await this.client.rpc<unknown>("video/task/sync", { taskId }),
        this.client.config.projectId,
      );
      const history = [...this.taskHistory.values()].find((item) => item.taskId === taskId);
      if (history) {
        this.upsertTaskHistory({
          requestId: history.requestId,
          taskId,
          state: result.status,
          reason: result.reason || "",
          ...(result.artifactManifest && result.artifactUrl
            ? { artifactManifest: result.artifactManifest, artifactUrl: result.artifactUrl }
            : {}),
        });
      }
      return result.artifactManifest?.artifactId || this.taskArtifactIds.get(taskId) || null;
    } catch (error) {
      dispatchVideoError(`视频原片地址刷新失败：${messageFrom(error)}`);
      return null;
    }
  }

  private async downloadCompletedArtifact(requestedArtifactId?: string, taskId?: string): Promise<void> {
    let artifactId = requestedArtifactId || (taskId ? this.taskArtifactIds.get(taskId) : null) || this.completedArtifactId;
    if (!artifactId && taskId) artifactId = await this.refreshHistoryTask(taskId);
    if (!artifactId) return;
    if (isTauri()) {
      if (this.exportInFlight) return;
      const manifest = this.artifactManifests.get(artifactId);
      const exportArtifact = window.BianhuaArtifactSync?.exportSavedArtifact;
      if (!manifest || !exportArtifact) {
        dispatchVideoError("原片尚未保存到客户端，请稍后重试");
        return;
      }
      if (!this.savedArtifactIds.has(artifactId)) {
        const transfer = this.artifactUrls.get(artifactId);
        if (transfer) {
          window.dispatchEvent(new CustomEvent("bianhua:artifact-sync-recovered", {
            detail: {
              manifest,
              url: { artifactId, ...transfer },
            },
          }));
        }
        window.dispatchEvent(new CustomEvent("bianhua:video-export", {
          detail: { status: "downloading", artifactId },
        }));
        return;
      }
      const button = document.querySelector<HTMLButtonElement>(`#video-panel [data-video-download][data-artifact-id="${CSS.escape(artifactId)}"]`);
      this.exportInFlight = true;
      button?.setAttribute("aria-busy", "true");
      if (button) button.disabled = true;
      try {
        const result = await exportArtifact({
          artifactId: manifest.artifactId,
          relativePath: manifest.relativePath,
          sha256: manifest.sha256,
          mimeType: manifest.mediaType,
        });
        window.dispatchEvent(new CustomEvent("bianhua:video-export", { detail: result }));
      } catch (error) {
        dispatchVideoError(error instanceof Error ? error.message : "原片导出失败");
      } finally {
        this.exportInFlight = false;
        button?.removeAttribute("aria-busy");
        this.updateDownloadButtons();
      }
      return;
    }
    let transfer = this.artifactUrls.get(artifactId);
    if (!transfer || Date.parse(transfer.expiresAt) <= Date.now()) {
      if (taskId) {
        const refreshedArtifactId = await this.refreshHistoryTask(taskId);
        if (refreshedArtifactId) {
          artifactId = refreshedArtifactId;
          transfer = this.artifactUrls.get(artifactId);
        }
      } else {
        this.scheduleTaskSync(0);
      }
      if (!transfer || Date.parse(transfer.expiresAt) <= Date.now()) {
        dispatchVideoError("视频下载地址尚未到达或已经过期，正在向服务器重新签发");
        return;
      }
    }
    if (this.exportInFlight) return;
    const button = document.querySelector<HTMLButtonElement>(`#video-panel [data-video-download][data-artifact-id="${CSS.escape(artifactId)}"]`);
    this.exportInFlight = true;
    button?.setAttribute("aria-busy", "true");
    if (button) button.disabled = true;
    try {
      const response = await fetch(transfer.url, { credentials: "omit", redirect: "error" });
      if (!response.ok) throw new Error(`下载原片失败（HTTP ${response.status}）`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `彼岸花·智体-${artifactId}.mp4`;
        link.rel = "noopener";
        document.body.append(link);
        link.click();
        link.remove();
      } finally {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
      }
      window.dispatchEvent(new CustomEvent("bianhua:video-export", {
        detail: { status: "exported", verified: true, destinationUri: "browser-download" },
      }));
    } catch (error) {
      dispatchVideoError(error instanceof Error ? error.message : "原片下载失败");
    } finally {
      this.exportInFlight = false;
      button?.removeAttribute("aria-busy");
      this.updateDownloadButtons();
    }
  }
}
